import {detectUiLayout} from 'swayok-react-mdb-ui-kit/helpers/detectUiLayout'
import type {UILayout} from 'swayok-react-mdb-ui-kit/types/Common'
import {create} from 'zustand/react'

// Тип состояния хранилища.
export interface UiLayoutStoreType extends UILayout {
    /**
     * Инициализировано ли хранилище.
     */
    initiated: boolean

    // API:

    // Инициализация при запуске приложения.
    init: () => void

    // Вызывается при изменении размеров окна. Определят изменения разметки.
    // Есть 3 разметки: desktop (bootstrap: xl, xxl), tablet (md, lg), mobile (xs, sm).
    // И модификатор: wide (tablet wide = bootstrap lg, desktop wide = bootstrap xxl).
    // Так же добавляется класс отображающий текущий bootstrap breakpoint (xs ... xxl).
    // В зависимости от размера меняется степень открытости (размер) бокового меню.
    updateUiLayout: () => void
}

// Размер окна отрисовки интерфейса в виде breakpoint.
export const useUiLayoutStore = create<UiLayoutStoreType>(
    (set, get) => ({
        /**
         * Инициализировано ли хранилище.
         */
        initiated: false,
        /**
         * Тип устройства.
         */
        deviceType: 'desktop',
        /**
         * У каждого типа имеется 2 версии - обычная и широкая.
         * Сопоставление примерно такое:
         * mobile = xxs|xs, mobile wide = sm;
         * tablet = md, tablet wide = lg;
         * desktop = xl, desktop wide = xxl;
         */
        isWide: false,
        /**
         * Размер устройства по брейкпоинтам Bootstrap CSS.
         */
        bootstrapSize: 'xl',

        // API:

        init(): void {
            const updateUiLayout = get().updateUiLayout
            updateUiLayout()
            if (get().initiated) {
                return
            }
            get().initiated = true
            // Мониторинг размеров окна. Так можно адаптировать интерфейс под новый размер.
            window.addEventListener('resize', updateUiLayout)
        },

        updateUiLayout(): void {
            const oldLayout: UILayout = {
                deviceType: get().deviceType,
                isWide: get().isWide,
                bootstrapSize: get().bootstrapSize,
            }
            const newLayout: UILayout = detectUiLayout()
            if (
                oldLayout.bootstrapSize === newLayout.bootstrapSize
                && oldLayout.deviceType === newLayout.deviceType
                && oldLayout.isWide === newLayout.isWide
                && document.body.classList.contains('layout-' + oldLayout.bootstrapSize)
                && document.body.classList.contains('layout-' + oldLayout.deviceType)
            ) {
                // Ничего не изменилось.
                return
            }
            set(newLayout)
            document.body.classList.remove(
                'layout-desktop',
                'layout-mobile',
                'layout-tablet',
                'wide',
                'layout-xs',
                'layout-xxs',
                'layout-sm',
                'layout-md',
                'layout-lg',
                'layout-xl',
                'layout-xxl'
            )
            document.body.classList.add(
                'layout-' + newLayout.deviceType,
                'layout-' + newLayout.bootstrapSize
            )
            if (newLayout.isWide) {
                document.body.classList.add('wide')
            }
        },
    })
)
